import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import { fmtUSD } from "../../pages/Budget";

export default function BudgetSnapshot() {
  const [data, setData] = useState(null);
  const year = String(new Date().getFullYear());

  useEffect(() => {
    api.get(`/budget/summary?year=${year}`).then(({ data }) => setData(data)).catch(() => {});
  }, [year]);

  if (!data) return null;
  const c = data.cards;

  const segs = [
    { v: c.spent_past, cls: "bg-ink" },
    { v: c.upcoming_confirmed, cls: "bg-ember" },
    { v: c.upcoming_planned, cls: "bg-hour" },
  ].filter((s) => s.v > 0);
  const segTotal = segs.reduce((a, s) => a + s.v, 0);

  return (
    <div className="bg-surface rounded-card border border-hairline" data-testid="budget-snapshot">
      {/* desktop */}
      <div className="hidden md:flex p-6 flex-wrap items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <Wallet size={16} className="text-ember" />
          <div>
            <p className="label-tag">Budget · {year}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-8">
          <div>
            <p className="label-tag mb-0.5">Spent</p>
            <p className="font-display font-medium text-xl tracking-tight" data-testid="snapshot-spent">{fmtUSD(c.spent_past)}</p>
          </div>
          <div>
            <p className="label-tag mb-0.5">Committed</p>
            <p className="font-display font-medium text-xl tracking-tight text-ember" data-testid="snapshot-committed">{fmtUSD(c.upcoming_confirmed)}</p>
          </div>
          <div>
            <p className="label-tag mb-0.5">Planned</p>
            <p className="font-display font-medium text-xl tracking-tight text-amber-600" data-testid="snapshot-planned">{fmtUSD(c.upcoming_planned)}</p>
          </div>
          <div>
            <p className="label-tag mb-0.5">Year total</p>
            <p className="font-display font-medium text-xl tracking-tight" data-testid="snapshot-total">{fmtUSD(c.total)}</p>
          </div>
        </div>
        <Link to="/insights/budget" className="btn-swiss btn-ghost text-sm" data-testid="snapshot-open-budget">
          Open budget <ArrowRight size={14} className="ml-2" />
        </Link>
      </div>

      {/* mobile */}
      <div className="md:hidden p-5" data-testid="budget-snapshot-mobile">
        <div className="flex items-center gap-2">
          <Wallet size={14} className="text-ember" />
          <p className="label-tag">Budget · {year}</p>
        </div>
        <div className="flex justify-between mt-4">
          <div>
            <p className="font-mono text-[9px] tracking-wide text-sand-400">SPENT</p>
            <p className="font-mono text-lg text-ink mt-0.5" data-testid="snapshot-m-spent">{fmtUSD(c.spent_past)}</p>
          </div>
          <div className="text-center">
            <p className="font-mono text-[9px] tracking-wide text-sand-400">COMMITTED</p>
            <p className="font-mono text-lg text-ember mt-0.5" data-testid="snapshot-m-committed">{fmtUSD(c.upcoming_confirmed)}</p>
          </div>
          <div className="text-right">
            <p className="font-mono text-[9px] tracking-wide text-sand-400">PLANNED</p>
            <p className="font-mono text-lg text-hour-deep mt-0.5" data-testid="snapshot-m-planned">{fmtUSD(c.upcoming_planned)}</p>
          </div>
        </div>
        {segTotal > 0 && (
          <div className="flex h-2 rounded-full overflow-hidden bg-sand-100 mt-4" data-testid="snapshot-m-bar">
            {segs.map((s, i) => (
              <div key={i} className={s.cls} style={{ width: `${(s.v / segTotal) * 100}%` }} />
            ))}
          </div>
        )}
        <div className="flex justify-between items-end mt-4">
          <div>
            <p className="font-mono text-[9px] tracking-wide text-sand-400">YEAR TOTAL</p>
            <p className="font-display font-medium text-3xl leading-none tracking-tight mt-1" data-testid="snapshot-m-total">{fmtUSD(c.total)}</p>
          </div>
          <Link to="/insights/budget" className="text-sm font-semibold text-ember" data-testid="snapshot-m-open-budget">
            Open budget →
          </Link>
        </div>
      </div>
    </div>
  );
}
